import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import MaterialReactTable from 'material-react-table'
import { MRT_Localization_PT } from 'material-react-table/locales/pt'
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
} from 'reactstrap'
import { IconButton } from '@mui/material'
import {
  Add,
  MonetizationOn,
  OpenInNew,
  People,
  Publish,
  Visibility,
  WorkOutline,
} from '@material-ui/icons'
import moment from 'moment'
import classnames from 'classnames'
import Breadcrumbs from '../../../components/Common/Breadcrumb'
import ModalPedidoCompra, {
  EQUIPE_CATEG_GR_ID,
  fetchPedidoCompraDocuments,
} from '../../../components/Modals/ModalPedidoCompra/ModalPedidoCompra'
import { abrirVagaInhireParaPedidoCompra } from '../../../components/Modals/ModalPedidoCompra/usePedidoCompraEquipeActions'
import ModalFormDisparoEmails from '../../../components/Modals/ModalFormDisparoEmails/ModalFormDisparoEmails'
import ModalFormContrForn from '../../../components/Modals/ModalFormContrForn/ModalFormContrForn'
import {
  compraCotacaoContrFornHidePropsComContratado,
} from '../../../components/Template/Doc/compraCotacaoTableUtils'
import { notifyError, notifySuccess } from '../../../utils/notify'
import { currencyFormatter } from '../../../utils/currencyFormatter'
import {
  fetchCandidatosByVagaIds,
  fetchCentroDeCustoOptions,
  fetchContrFornStatusRows,
  fetchInhireCountsByVaga,
  fetchVagasRh,
} from './gestaoVagasApi'
import {
  ETAPA_RECEBIDOS,
  ETAPAS_FUNIL,
  classifyCandidatoEtapa,
  countEtapas,
  formatContratoLabel,
  matchesVagaFilter,
  resolveAtivoStatusIds,
} from './gestaoVagasHelpers'

const kpiCardStyle = {
  border: '1px solid #e9ecef',
  borderRadius: 6,
  padding: '12px 14px',
  height: '100%',
}

const kpiLabelStyle = {
  fontSize: 12,
  color: '#74788d',
  marginBottom: 4,
}

const kpiValueStyle = {
  fontSize: 22,
  fontWeight: 600,
  color: '#273351',
  lineHeight: 1.2,
}

const mrtCompactProps = {
  muiTableBodyCellProps: { sx: { fontSize: '11px' } },
  muiTableHeadCellProps: {
    sx: {
      '.MuiSvgIcon-root': { fontSize: '18px' },
      fontSize: '12px',
      fontWeight: 'normal',
      padding: '8px',
    },
  },
  muiTablePaginationProps: {
    sx: {
      '.MuiTablePagination-displayedRows': { fontSize: '12px' },
      marginTop: '0px',
      fontSize: '12px',
    },
    labelRowsPerPage: <span style={{ fontSize: '12px' }}>Linhas por página</span>,
    rowsPerPageOptions: [10, 31, 50],
  },
  enableDensityToggle: false,
  enableColumnFilters: false,
  positionPagination: 'bottom',
}

const normalizeVaga = (row) => ({
  ...row,
  vaga_id: Number(row.vaga_id) || 0,
  pedidoCompra_desc: row.pedidoCompra_desc || row.vaga_descricao || row.vaga_nome || '',
  vaga_cargo: row.vaga_cargo || row.vaga_nome || row.pedidoCompra_desc || '',
  contrato_label: formatContratoLabel(row),
  contrato_interno: !Number(row.contrForn_id) && Number(row.contratoForn_id) > 0,
  email_enviado_count: Number(row.email_enviado_count) || 0,
})

const GestaoVagas = () => {
  const user = useSelector((state) => state.auth.user)
  const allowed = Boolean(user)

  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [vagas, setVagas] = useState([])
  const [candidatos, setCandidatos] = useState([])
  const [inhireCounts, setInhireCounts] = useState({})
  const [inhireErrors, setInhireErrors] = useState({})
  const [inhireLoading, setInhireLoading] = useState(false)
  const [ativoStatusIds, setAtivoStatusIds] = useState(() => new Set())
  const [filterText, setFilterText] = useState('')
  const [selectedVagaId, setSelectedVagaId] = useState(null)
  const [selectedEtapa, setSelectedEtapa] = useState(ETAPA_RECEBIDOS)
  const [selCentroDeCusto, setSelCentroDeCusto] = useState([])
  const [publishOpen, setPublishOpen] = useState(false)
  const [viewVagaRow, setViewVagaRow] = useState(null)
  const [addCandidatoRow, setAddCandidatoRow] = useState(null)
  const [openingInhireId, setOpeningInhireId] = useState(null)

  const loadPage = useCallback(async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const [statusRows, vagaRows, centros] = await Promise.all([
        fetchContrFornStatusRows(),
        fetchVagasRh(),
        fetchCentroDeCustoOptions(),
      ])

      const normalizedVagas = (vagaRows || []).map(normalizeVaga)
      setAtivoStatusIds(resolveAtivoStatusIds(statusRows))
      setVagas(normalizedVagas)
      setSelCentroDeCusto(centros || [])

      const candidatoRows = await fetchCandidatosByVagaIds(
        normalizedVagas.map((vaga) => vaga.vaga_id),
      )
      setCandidatos(Array.isArray(candidatoRows) ? candidatoRows : [])
      setLoading(false)

      setInhireLoading(true)
      const inhire = await fetchInhireCountsByVaga(normalizedVagas)
      setInhireCounts(inhire.counts || {})
      setInhireErrors(inhire.errors || {})
      setInhireLoading(false)
    } catch (error) {
      console.error('Erro ao carregar gestão de vagas RH:', error)
      notifyError('Não foi possível carregar as vagas.')
      setLoadError(true)
      setVagas([])
      setCandidatos([])
      setLoading(false)
      setInhireLoading(false)
    }
  }, [])

  useEffect(() => {
    if (allowed) loadPage()
  }, [allowed, loadPage])

  const filteredVagas = useMemo(
    () => vagas.filter((vaga) => matchesVagaFilter(vaga, filterText)),
    [vagas, filterText],
  )

  const filteredVagaIds = useMemo(
    () => new Set(filteredVagas.map((vaga) => Number(vaga.vaga_id))),
    [filteredVagas],
  )

  const candidatosNoRecorte = useMemo(
    () => candidatos.filter((candidato) => filteredVagaIds.has(Number(candidato.vaga_id))),
    [candidatos, filteredVagaIds],
  )

  const kpis = useMemo(() => {
    const etapas = countEtapas(candidatosNoRecorte, ativoStatusIds)
    return {
      vagas: filteredVagas.length,
      recebidos: etapas.recebidos,
      selecionados: etapas.selecionados,
      contratado: etapas.contratado,
    }
  }, [filteredVagas, candidatosNoRecorte, ativoStatusIds])

  const selectedVaga = useMemo(
    () => filteredVagas.find((vaga) => Number(vaga.vaga_id) === Number(selectedVagaId)) || null,
    [filteredVagas, selectedVagaId],
  )

  const candidatosDaVaga = useMemo(
    () => candidatos.filter((candidato) => Number(candidato.vaga_id) === Number(selectedVagaId)),
    [candidatos, selectedVagaId],
  )

  const candidatosDaEtapa = useMemo(
    () => candidatosDaVaga.filter(
      (candidato) => classifyCandidatoEtapa(candidato, ativoStatusIds) === selectedEtapa,
    ),
    [candidatosDaVaga, ativoStatusIds, selectedEtapa],
  )

  const handleSelectVaga = (vaga, etapa = ETAPA_RECEBIDOS) => {
    setSelectedVagaId(Number(vaga.vaga_id) || null)
    setSelectedEtapa(etapa)
  }

  const handlePublishSuccess = async () => {
    setPublishOpen(false)
    await loadPage()
  }

  const handleAddCandidatoSuccess = async () => {
    setAddCandidatoRow(null)
    notifySuccess('Candidato adicionado.')
    await loadPage()
  }

  const handleAbrirInhire = async (vaga) => {
    const pedidoCompraId = Number(vaga?.pedidoCompra_id) || 0
    if (!pedidoCompraId) {
      notifyError('Não foi possível identificar o pedido desta vaga.')
      return
    }

    setOpeningInhireId(Number(vaga.vaga_id))
    try {
      const result = await abrirVagaInhireParaPedidoCompra(pedidoCompraId)
      if (result?.ok) {
        notifySuccess(result.alreadyExists
          ? 'Esta vaga já estava aberta no inHire.'
          : 'Vaga aberta no inHire.')
        await loadPage()
      }
    } finally {
      setOpeningInhireId(null)
    }
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'vaga_id',
      header: 'Código',
      size: 70,
    },
    {
      accessorKey: 'vaga_cargo',
      header: 'Cargo / função',
      Cell: ({ row }) => row.original.vaga_cargo || '-',
    },
    {
      accessorKey: 'proj_nome',
      header: 'Projeto',
      Cell: ({ row }) => row.original.proj_nome || '-',
    },
    {
      id: 'contrato',
      header: 'Contrato',
      Cell: ({ row }) => {
        if (!row.original.contrato_label) return '-'
        return (
          <span>
            {row.original.contrato_label}
            {row.original.contrato_interno ? (
              <span className="text-muted" style={{ fontSize: 11 }}> · só interno</span>
            ) : null}
          </span>
        )
      },
    },
    {
      accessorKey: 'pedidoCompra_dtSolicitacao',
      header: 'Abertura',
      Cell: ({ row }) => (
        row.original.pedidoCompra_dtSolicitacao
          ? moment(row.original.pedidoCompra_dtSolicitacao).format('DD/MM/YYYY')
          : '-'
      ),
    },
    {
      accessorKey: 'pf_nomeExibicao',
      header: 'Responsável técnico',
      Cell: ({ row }) => row.original.pf_nomeExibicao || '-',
    },
    {
      id: 'emails',
      header: 'E-mails',
      enableColumnActions: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const count = Number(row.original.email_enviado_count) || 0
        if (!Number(row.original.pedidoCompra_id)) return '-'
        return (
          <div
            className="d-flex align-items-center"
            style={{ gap: 4 }}
            onClick={(event) => event.stopPropagation()}
          >
            <span>{count || '—'}</span>
            {Number(row.original.pedidoCompraStatus_id) === 5 ? (
              <ModalFormDisparoEmails
                pedidoCompraData={row.original}
                fetchPedidoCompraDocuments={fetchPedidoCompraDocuments}
                onEmailsSent={loadPage}
              />
            ) : null}
          </div>
        )
      },
    },
    {
      id: 'inhire',
      header: 'Candidatos inHire',
      enableColumnActions: false,
      Cell: ({ row }) => {
        const vagaId = Number(row.original.vaga_id)
        const hasInhire = Boolean(String(row.original.vaga_inhireId || '').trim())
        if (!hasInhire) return '—'
        if (inhireLoading && inhireCounts[vagaId] == null && !inhireErrors[vagaId]) {
          return <Spinner size="sm" />
        }
        if (inhireErrors[vagaId]) return '—'
        if (inhireCounts[vagaId] == null) return '—'
        return String(inhireCounts[vagaId])
      },
    },
    {
      id: 'acoes',
      header: 'Ações',
      enableColumnActions: false,
      enableSorting: false,
      Cell: ({ row }) => {
        const vaga = row.original
        const inhireId = String(vaga.vaga_inhireId || '').trim()
        const isOpening = Number(openingInhireId) === Number(vaga.vaga_id)

        return (
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ gap: 2 }}
            onClick={(event) => event.stopPropagation()}
          >
            <IconButton
              size="small"
              title="Visualizar candidatos"
              onClick={(event) => {
                event.stopPropagation()
                handleSelectVaga(vaga, ETAPA_RECEBIDOS)
              }}
            >
              <People fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Adicionar candidato"
              onClick={(event) => {
                event.stopPropagation()
                setAddCandidatoRow(vaga)
              }}
            >
              <Add fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title="Visualizar vaga"
              onClick={(event) => {
                event.stopPropagation()
                setViewVagaRow(vaga)
              }}
            >
              <Visibility fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              title={inhireId ? 'Vaga já aberta no inHire' : 'Abrir vaga no inHire'}
              disabled={isOpening}
              onClick={(event) => {
                event.stopPropagation()
                handleAbrirInhire(vaga)
              }}
            >
              {isOpening ? <Spinner size="sm" /> : <WorkOutline fontSize="small" />}
            </IconButton>
            {inhireId ? (
              <IconButton
                size="small"
                title="Abrir job no inHire"
                onClick={(event) => {
                  event.stopPropagation()
                  window.open(
                    `https://ecossis.inhire.app/jobs/${encodeURIComponent(inhireId)}`,
                    '_blank',
                    'noopener,noreferrer',
                  )
                }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            ) : null}
          </div>
        )
      },
    },
  ], [inhireCounts, inhireErrors, inhireLoading, loadPage, openingInhireId])

  if (!allowed) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Gestão de Vagas"
            breadcrumbItems={[
              { title: 'RH', link: '/recursos-humanos/dashboard' },
              { title: 'Gestão de Vagas' },
            ]}
          />
          <Card>
            <CardBody>Sem permissão para acessar esta tela.</CardBody>
          </Card>
        </Container>
      </div>
    )
  }

  return (
    <div className="page-content">
        <Breadcrumbs
          title="Gestão de Vagas"
          breadcrumbItems={[
            { title: 'RH', link: '/rh-gestao-vagas' },
            { title: 'Gestão de Vagas' },
          ]}
        />

        <Row className="mb-3 align-items-end">
          <Col md={6}>
            <Input
              bsSize="sm"
              placeholder="Filtrar por código, cargo, projeto, contrato ou responsável"
              value={filterText}
              onChange={(event) => setFilterText(event.target.value)}
            />
          </Col>
          <Col md={6} className="text-md-right mt-2 mt-md-0">
            <Button color="primary" size="sm" onClick={() => setPublishOpen(true)}>
              <Publish fontSize="small" className="mr-1" />
              Criar vaga
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          {[
            { label: 'Vagas', value: kpis.vagas },
            { label: 'Recebidos', value: kpis.recebidos },
            { label: 'Selecionados', value: kpis.selecionados },
            { label: 'Contratados', value: kpis.contratado },
          ].map((kpi) => (
            <Col key={kpi.label} md={3} sm={6} className="mb-2">
              <div style={kpiCardStyle}>
                <div style={kpiLabelStyle}>{kpi.label}</div>
                <div style={kpiValueStyle}>{loading ? '—' : kpi.value}</div>
              </div>
            </Col>
          ))}
        </Row>

        {loadError ? (
          <Card>
            <CardBody>Erro ao carregar as vagas. Tente recarregar a página.</CardBody>
          </Card>
        ) : (
          <Row>
            <Col lg={8} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  {loading ? (
                    <div className="d-flex align-items-center justify-content-center py-5">
                      <Spinner color="primary" className="mr-2" />
                      Carregando vagas...
                    </div>
                  ) : filteredVagas.length <= 0 ? (
                    <div className="text-muted py-4 text-center">
                      Nenhuma vaga neste recorte.
                    </div>
                  ) : (
                    <MaterialReactTable
                      columns={columns}
                      data={filteredVagas}
                      localization={MRT_Localization_PT}
                      getRowId={(row) => String(row.vaga_id)}
                      enableTopToolbar={false}
                      {...mrtCompactProps}
                      muiTableBodyRowProps={({ row }) => ({
                        onClick: () => handleSelectVaga(row.original, selectedEtapa),
                        sx: {
                          cursor: 'pointer',
                          backgroundColor: Number(row.original.vaga_id) === Number(selectedVagaId)
                            ? '#e8f4f8'
                            : undefined,
                        },
                      })}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col lg={4} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <h6 className="mb-3">Funil da vaga</h6>
                  {!selectedVaga ? (
                    <div className="text-muted">Selecione uma vaga para ver os candidatos por etapa.</div>
                  ) : (
                    <>
                      <div className="mb-2">
                        <strong>{selectedVaga.vaga_cargo || `Vaga ${selectedVaga.vaga_id}`}</strong>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {selectedVaga.proj_nome || 'Sem projeto'}
                        </div>
                      </div>
                      <Nav pills className="mb-3 flex-wrap">
                        {ETAPAS_FUNIL.map((etapa) => (
                          <NavItem key={etapa.id}>
                            <NavLink
                              href="#"
                              className={classnames({ active: selectedEtapa === etapa.id })}
                              onClick={(event) => {
                                event.preventDefault()
                                setSelectedEtapa(etapa.id)
                              }}
                            >
                              {etapa.label}
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                      {candidatosDaEtapa.length <= 0 ? (
                        <div className="text-muted">Nenhum candidato nesta etapa.</div>
                      ) : (
                        candidatosDaEtapa.map((candidato) => (
                          <div
                            key={candidato.candidato_id}
                            className="d-flex align-items-center justify-content-between py-2"
                            style={{ borderBottom: '1px solid #f0f2f5' }}
                          >
                            <div>
                              <div>{candidato.candidato_nome || candidato.compraCotacao_nomeInteressado || 'Candidato'}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {candidato.candidato_dtCria
                                  ? moment(candidato.candidato_dtCria).format('DD/MM/YYYY')
                                  : '—'}
                                {' · '}
                                {currencyFormatter.format(Number(
                                  candidato.candidato_valorSalario
                                  || candidato.compraCotacao_vlr
                                  || 0,
                                ))}
                                {candidato.contrFornStatus_nome
                                  ? ` · ${candidato.contrFornStatus_nome}`
                                  : ''}
                              </div>
                            </div>
                            {selectedEtapa === ETAPA_RECEBIDOS && Number(candidato.compraCotacao_id) > 0 ? (
                              <ModalFormContrForn
                                compraCotacaoData={{
                                  ...candidato,
                                  compraCotacao_id: candidato.compraCotacao_id,
                                  compraCotacao_nomeInteressado: candidato.compraCotacao_nomeInteressado
                                    || candidato.candidato_nome,
                                  compraCotacao_vlr: candidato.compraCotacao_vlr
                                    ?? candidato.candidato_valorSalario,
                                  orcItem_id: candidato.orcItem_id || selectedVaga.orcItem_id,
                                }}
                                pedidoCompraData={{
                                  ...selectedVaga,
                                  orcItem_id: selectedVaga.orcItem_id,
                                  pf_emailProf: selectedVaga.pf_emailProf,
                                  vaga_id: selectedVaga.vaga_id,
                                }}
                                showEquipePedidoCompraFields
                                criarEmailCorporativo={(
                                  Number(selectedVaga.vaga_office365) === 1
                                  && Number(selectedVaga.vaga_emailCorp) === 1
                                )}
                                centroDeCustoDisabled={Boolean(selectedVaga.orcItem_id)}
                                orcItemDisabled={Boolean(selectedVaga.orcItem_id)}
                                {...compraCotacaoContrFornHidePropsComContratado}
                                onSuccess={async () => {
                                  notifySuccess('Contratação iniciada.')
                                  await loadPage()
                                }}
                              />
                            ) : Number(candidato.contrForn_id) > 0 ? (
                              <IconButton size="small" disabled title="Candidato já possui contrato">
                                <MonetizationOn fontSize="small" />
                              </IconButton>
                            ) : null}
                          </div>
                        ))
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <ModalPedidoCompra
          isOpen={publishOpen}
          onClose={() => setPublishOpen(false)}
          showEquipePedidoCompraFields
          showSkipCotacao={false}
          hidePropItem
          hideQuantidadeOportunidades
          hideDtLimite
          selCentroDeCusto={selCentroDeCusto}
          filterOrcItemCategGrId={EQUIPE_CATEG_GR_ID}
          title="Criar vaga"
          submitLabel="Criar vaga"
          user={{ pf_id: user?.pf_id }}
          onSuccess={handlePublishSuccess}
        />

        <ModalPedidoCompra
          isOpen={Boolean(viewVagaRow)}
          onClose={() => setViewVagaRow(null)}
          editPedidoCompraRow={viewVagaRow}
          showEquipePedidoCompraFields
          showSkipCotacao={false}
          selCentroDeCusto={selCentroDeCusto}
          hidePropItem
          hideQuantidadeOportunidades
          hideDtLimite
          readOnly
          readOnlyOrcItem
          title={`Visualizar vaga${viewVagaRow?.proj_nome ? ` - ${viewVagaRow.proj_nome}` : ''}`}
          user={{ pf_id: user?.pf_id }}
          onSuccess={loadPage}
        />

        <ModalPedidoCompra
          isOpen={Boolean(addCandidatoRow)}
          onClose={() => setAddCandidatoRow(null)}
          editPedidoCompraRow={addCandidatoRow}
          showEquipePedidoCompraFields
          showSkipCotacao={false}
          hideCentroDeCusto
          hidePropItem
          hideQuantidadeOportunidades
          hideDtLimite
          modoCotacao
          cotacaoParaPedidoExistente
          title="Adicionar candidato"
          submitLabel="Adicionar candidato"
          user={{ pf_id: user?.pf_id }}
          onSuccess={handleAddCandidatoSuccess}
        />
    </div>
  )
}

export default GestaoVagas
